/* Adapted from OldSchoolSDK/osrs-sdk Pathing.ts & Collision.ts, GPL-3.0.
 * Modified 2026-09-10: dependency injection, bounded queue, no global tile cache,
 * corrected fallback bound, and typed southwest tile geometry. */
import type {Tile,Entity} from '../../engine/types';
export const directions=[{x:-1,y:0},{x:1,y:0},{x:0,y:1},{x:0,y:-1},{x:-1,y:1},{x:1,y:1},{x:-1,y:-1},{x:1,y:-1}];
export function collisionMath(x:number,y:number,s:number,x2:number,y2:number,s2:number){return !(x>x2+s2-1||x+s-1<x2||y-s+1>y2||y<y2-s2+1);}
export function contains(e:Pick<Entity,'tile'|'size'>,t:Tile){return collisionMath(t.x,t.y,1,e.tile.x,e.tile.y,e.size);}
export function distance(t:Tile,e:Pick<Entity,'tile'|'size'>):number{return Math.max(Math.max(e.tile.x-t.x,0,t.x-(e.tile.x+e.size-1)),Math.max(e.tile.y-e.size+1-t.y,0,t.y-e.tile.y));}
export function meleeAdjacent(t:Tile,e:Pick<Entity,'tile'|'size'>):boolean{
 const x=t.x>=e.tile.x&&t.x<e.tile.x+e.size,y=t.y<=e.tile.y&&t.y>e.tile.y-e.size;
 return (x&&(t.y===e.tile.y+1||t.y===e.tile.y-e.size))||(y&&(t.x===e.tile.x-1||t.x===e.tile.x+e.size));
}
export type Passable=(t:Tile)=>boolean;
/** Breadth-first path search, with OSRS W,E,S,N,SW,SE,NW,NE tie ordering. */
export function constructPath(start:Tile,ends:Tile[],passable:Passable):Tile[]{
 if(!ends.length)return [];
 type Node=Tile&{parent:number;length:number};
 const nodes:Node[]=[{...start,parent:-1,length:0}], seen=new Set([`${start.x},${start.y}`]);
 const targets=new Set(ends.map(t=>`${t.x},${t.y}`));let found=-1,closest=-1,minDist=Infinity,minLength=100;
 for(let head=0;head<nodes.length&&head<16384;head++){
  const p=nodes[head];if(targets.has(`${p.x},${p.y}`)){found=head;break;}
  for(const end of ends){const dx=p.x-end.x,dy=p.y-end.y,d=dx*dx+dy*dy;if(Math.abs(dx)<=10&&Math.abs(dy)<=10&&p.length<100&&(d<minDist||(d===minDist&&p.length<minLength))){minDist=d;minLength=p.length;closest=head;}}
  for(let i=0;i<directions.length;i++){
   const dir=directions[i],q={x:p.x+dir.x,y:p.y+dir.y},key=`${q.x},${q.y}`;
   if(seen.has(key)||!passable(q))continue;
   if(i>=4&&(!passable({x:p.x,y:q.y})||!passable({x:q.x,y:p.y})))continue;
   seen.add(key);nodes.push({...q,parent:head,length:p.length+1});
  }
 }
 const path:Tile[]=[];for(let i=found>=0?found:closest;i>0;i=nodes[i].parent)path.push({x:nodes[i].x,y:nodes[i].y});return path.reverse();
}
/** NPCs use greedy pursuit, not the player's breadth-first route finder. */
export function npcStep(start:Tile,target:Tile,passable:Passable):Tile{
 const dx=Math.sign(target.x-start.x),dy=Math.sign(target.y-start.y),both={x:start.x+dx,y:start.y+dy};
 if(dx&&dy&&passable(both)&&passable({x:both.x,y:start.y})&&passable({x:start.x,y:both.y}))return both;
 if(dx&&passable({x:start.x+dx,y:start.y}))return {x:start.x+dx,y:start.y};
 if(dy&&passable({x:start.x,y:start.y+dy}))return {x:start.x,y:start.y+dy};return start;
}
